import { createReducer, on } from '@ngrx/store';
import {
  CompanyRoleType,
  CompanyRoleValue,
  UserRole,
} from '../../models/user.model';
import { UserService } from '../../state-services/user.service';
import { LoadingState, ProfileState } from '../store.states';
import * as ProfileActions from './profile.actions';

const mapCompanyRoleNumberToUserRole = (
  companyRoleNumber: number | undefined,
): UserRole => {
  switch (companyRoleNumber) {
    case 1:
      return UserRole.ADMIN;
    case 2:
      return UserRole.MAINTAINER;
    default:
      return UserRole.PILOT;
  }
};

const extractCompanyRoleNumber = (
  companyRole: CompanyRoleType | CompanyRoleValue | undefined,
): number | undefined =>
  typeof companyRole === 'object'
    ? Number(companyRole.role)
    : companyRole === undefined
      ? undefined
      : Number(companyRole);

export const initialState: ProfileState = {
  role: UserRole.PILOT,
  id: '',
  companyId: '',
  family_name: '',
  given_name: '',
  avatar: '',
  email: '',
  firstName: '',
  jobTitle: '',
  lastName: '',
  isMasteradmin: false,
  privilegedRole: null,
  loadingState: LoadingState.FUTURE,
  quantumProfileLoadingState: LoadingState.FUTURE,
  updatedState: LoadingState.FUTURE,
  roleLoadingState: LoadingState.FUTURE,
  activeProfileError: '',
  quantumProfileError: '',
  completed: false,
};
export const profileReducer = createReducer(
  initialState,
  on(ProfileActions.logout, (state) => ({
    ...state,
    companyId: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    avatar: '',
    loadingState: LoadingState.SUCCESS,
  })),
  on(ProfileActions.freezingApiAfterImpersonation, (state) => ({
    ...state,
    apiFrozen: true,
  })),
  on(ProfileActions.loadingMsalActiveProfile, (state) => ({
    ...state,
    loadingState: LoadingState.PENDING,
    updatedState: LoadingState.FUTURE,
  })),
  on(
    ProfileActions.loadingMsalActiveProfileSuccess,
    (state, { msalProfile }) => ({
      ...state,
      firstName: state.firstName ? state.firstName : msalProfile.given_name,
      lastName: state.lastName ? state.lastName : msalProfile.family_name,
      given_name: msalProfile.given_name,
      family_name: msalProfile.family_name,
      // Kept if the backend profile already landed, like the name above it: the token names the
      // logged-in account, which is not who the request acts as while impersonating.
      email: state.email ? state.email : msalProfile.email,
      loadingState: LoadingState.SUCCESS,
    }),
  ),
  on(ProfileActions.loadingQuantumProfile, (state) => ({
    ...state,
    loadingState: LoadingState.PENDING,
    updatedState: LoadingState.FUTURE,
    quantumProfileLoadingState: LoadingState.PENDING,
    roleLoadingState: LoadingState.PENDING,
  })),
  on(
    ProfileActions.loadingQuantumProfileSuccess,
    (state, { quantumProfile }) => ({
      ...state,
      companyId: quantumProfile.companyId ?? quantumProfile.company?.id ?? '',
      firstName: quantumProfile.firstName ?? state.firstName,
      lastName: quantumProfile.lastName ?? state.lastName,
      email: quantumProfile.email ?? state.email,
      avatar: quantumProfile.avatar,
      jobTitle: quantumProfile.jobTitle,
      isMasteradmin: quantumProfile.isMasteradmin ?? false,
      privilegedRole: quantumProfile.privilegedRole ?? null,
      impersonatedBy: quantumProfile.impersonatedBy,
      impersonationExpiresAt: quantumProfile.impersonationExpiresAt,
      id: quantumProfile.id,
      role: mapCompanyRoleNumberToUserRole(
        extractCompanyRoleNumber(quantumProfile.companyRole),
      ),
      loadingState: LoadingState.SUCCESS,
      completed: UserService.isProfileComplete({
        id: quantumProfile.id,
        avatar: '',
        companyName: '',
        country: '',
        fieldsOfApplication: [],
        firstName: quantumProfile.firstName,
        industry: '',
        jobTitle: quantumProfile.jobTitle,
        lastName: quantumProfile.lastName,
        isMasteradmin: quantumProfile.isMasteradmin ?? false,
        stateOrProvince: '',
      }),
      quantumProfileLoadingState: LoadingState.SUCCESS,
      roleLoadingState: LoadingState.SUCCESS,
    }),
  ),
  on(ProfileActions.loadingMsalActiveProfileError, (state, { error }) => ({
    ...state,
    activeProfileError: error,
    loadingState: LoadingState.ERROR,
    quantumProfileLoadingState: LoadingState.ERROR,
  })),
  on(ProfileActions.loadingQuantumProfileError, (state, { error }) => ({
    ...state,
    quantumProfileError: error,
    loadingState: LoadingState.ERROR,
    // Both states are moved to PENDING by loadingQuantumProfile, so both have to be resolved
    // here: consumers that wait for the profile to settle would otherwise wait forever.
    quantumProfileLoadingState: LoadingState.ERROR,
    roleLoadingState: LoadingState.ERROR,
  })),
  on(ProfileActions.editingQuantumProfile, (state) => ({
    ...state,
    updatedState: LoadingState.PENDING,
  })),
  on(
    ProfileActions.editingQuantumProfileSuccess,
    (state, { updatedQuantumProfile }) => ({
      ...state,
      firstName: updatedQuantumProfile.firstName,
      lastName: updatedQuantumProfile.lastName,
      avatar: updatedQuantumProfile.avatar,
      jobTitle: updatedQuantumProfile.jobTitle,
      updatedState: LoadingState.SUCCESS,
      completed: UserService.isProfileComplete({
        id: updatedQuantumProfile.id,
        avatar: '',
        companyName: '',
        country: '',
        fieldsOfApplication: [],
        firstName: updatedQuantumProfile.firstName,
        industry: '',
        isMasteradmin: state.isMasteradmin,
        jobTitle: updatedQuantumProfile.jobTitle,
        lastName: updatedQuantumProfile.lastName,
        stateOrProvince: '',
      }),
    }),
  ),
  on(ProfileActions.editingQuantumProfileError, (state, { error }) => ({
    ...state,
    quantumProfileError: error,
    updatedState: LoadingState.ERROR,
  })),
  on(ProfileActions.loadingProfileRole, (state) => ({
    ...state,
    roleLoadingState: LoadingState.PENDING,
  })),
  on(ProfileActions.loadingProfileRoleSuccess, (state, { role }) => ({
    ...state,
    role: role,
    roleLoadingState: LoadingState.SUCCESS,
  })),
  on(ProfileActions.loadingProfileRoleError, (state, { error }) => ({
    ...state,
    quantumProfileError: error,
    roleLoadingState: LoadingState.ERROR,
  })),
  on(ProfileActions.updatingProfileRole, (state) => ({
    ...state,
    updatedState: LoadingState.PENDING,
  })),
  on(ProfileActions.updatingProfileRoleSuccess, (state, { role }) => ({
    ...state,
    updatedState: LoadingState.SUCCESS,
  })),
  on(ProfileActions.updatingProfileRoleError, (state, { error }) => ({
    ...state,
    updatedState: LoadingState.ERROR,
  })),
);
